import { Badge, Button, Text, mergeClasses } from "@fluentui/react-components";
import type { MeetingEvent } from "../services/graphService";
import { useUiStyles, SectionTitle, IconTile, MetaRow, CalendarIcon, ClockIcon } from "./ui";
import { useLanguage } from "../i18n/LanguageContext";

function formatRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const day = start.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const time = (d: Date) =>
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${day} · ${time(start)}–${time(end)}`;
}

export function MeetingsList({ meetings }: { meetings: MeetingEvent[] }) {
  const s = useUiStyles();
  const { t } = useLanguage();

  if (meetings.length === 0) {
    return (
      <section className={s.section}>
        <SectionTitle>{t("meetings.title")}</SectionTitle>
        <div className={mergeClasses(s.card, s.emptyCard)}>
          <IconTile variant="neutral">
            <CalendarIcon />
          </IconTile>
          <Text size={200}>{t("meetings.empty")}</Text>
        </div>
      </section>
    );
  }

  return (
    <section className={s.section}>
      <SectionTitle count={meetings.length}>{t("meetings.title")}</SectionTitle>
      <div className={s.stack}>
        {meetings.map((meeting) => (
          <div key={meeting.id} className={s.card}>
            <div className={s.row}>
              <IconTile variant="brand">
                <CalendarIcon />
              </IconTile>
              <div className={s.grow}>
                <div className={s.rowBetween}>
                  <Text weight="semibold" block truncate wrap={false} className={s.grow}>
                    {meeting.subject}
                  </Text>
                  {meeting.isOnline && (
                    <Badge appearance="tint" color="brand" size="small">
                      {t("meetings.online")}
                    </Badge>
                  )}
                </div>
                <MetaRow>
                  <ClockIcon />
                  <span>
                    {formatRange(meeting.start, meeting.end)} · {meeting.organizer}
                    {meeting.attendeeCount > 0 ? ` · ${meeting.attendeeCount} ${t("meetings.attendees")}` : ""}
                  </span>
                </MetaRow>
                {meeting.joinUrl && (
                  <div style={{ marginTop: "10px" }}>
                    <Button
                      as="a"
                      href={meeting.joinUrl}
                      target="_blank"
                      rel="noreferrer"
                      size="small"
                      appearance="primary"
                    >
                      {t("meetings.join")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
